/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';

import { useGetMatchRosterRoundQuery } from 'common/store/modules/api/cfs-api';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import { getTranslation } from 'common/react/utils/translations';

import { TLCondensedCta } from '../TLCondensedCta';
import { InsOuts } from '../InsOuts/InsOuts';
import { TLCondensedLastUpdatedSkeleton } from '../skeleton-loaders/TLCondensedLastUpdatedSkeleton';
import { InsOutsSingleSkeleton } from '../skeleton-loaders/InsOutsSingleSkeleton';
import { InsOutsBothSkeleton } from '../skeleton-loaders/InsOutsBothSkeleton';
import { TLCondensedCtaSkeleton } from '../skeleton-loaders/TLCondensedCtaSkeleton';
import { TLCondensedHeader } from '../TLCondensedHeader';
import { TLCondensedHeaderSkeleton } from '../skeleton-loaders/TLCondensedHeaderSkeleton';
import { LastUpdated } from 'common/react/components/LastUpdated';
import { Sponsor } from 'common/react/components/sponsor/Sponsor';

/**
 * Helper to build CTA configuration safely without nested ternaries.
 *
 * @param ctaDisabled
 * @param ctaCustomLink
 * @param ctaDefaultLink
 * @param ctaCustomText
 */
const getCtaConfig = (
    ctaDisabled,
    ctaCustomLink,
    ctaDefaultLink,
    ctaCustomText
) => {
    if (ctaDisabled) {
        return null;
    }

    const defaultText = getTranslation('label.teamLineups.viewFull');

    return {
        ctaLink: ctaCustomLink || ctaDefaultLink,
        ctaText: ctaCustomText || defaultText,
        ctaDefaultPath: ctaCustomLink ? 'false' : 'true'
    };
};

/**
 * Fetches the match roster data.
 *
 * @param root0
 * @param root0.matchId
 * @param root0.matchTitle
 * @param root0.matchUrl
 * @param root0.roundName
 * @param root0.providerMatchId
 * @param root0.providerRoundId
 * @param root0.homeTeamId
 * @param root0.awayTeamId
 * @param root0.homeTeamName
 * @param root0.awayTeamName
 * @param root0.teamDisplay
 * @param root0.ctaDefaultPath
 * @param root0.ctaDisabled
 * @param root0.ctaCustomLink
 * @param root0.ctaCustomText
 * @param root0.startTime
 * @param root0.venueName
 * @param root0.venueLocation
 * @param root0.venueLandOwner
 * @param root0.sponsorPromoId
 * @param root0.competitionId
 */
export const TLCondensed = ({
    matchId,
    matchTitle,
    matchUrl,
    roundName,
    providerMatchId,
    providerRoundId,
    homeTeamId,
    awayTeamId,
    homeTeamName,
    awayTeamName,
    teamDisplay,
    ctaDefaultPath,
    ctaDisabled,
    ctaCustomLink,
    ctaCustomText,
    startTime,
    venueName,
    venueLocation,
    venueLandOwner,
    sponsorPromoId,
    competitionId
}) => {
    const compAbbr = PULSE.app.common.match.getCompAbbr(competitionId);
    const isAFLW = compAbbr.toLowerCase() === 'aflw';

    const {
        data: rosterData,
        isFetching: isFetchingMatchRosterData,
        isError: matchRosterDataError
    } = useGetMatchRosterRoundQuery(
        { roundId: providerRoundId },
        { skip: !providerRoundId }
    );

    const matchRoster = rosterData?.find(
        (roster) => roster.match.matchId === providerMatchId
    )?.matchRoster;

    const selectedRegion = useSingleSiteSetting('region');
    const lastUpdated = moment(matchRoster?.lastUpdated);

    const insOutsExists =
        (matchRoster?.homeTeam?.ins?.length ?? 0) > 0 ||
        (matchRoster?.homeTeam?.outs?.length ?? 0) > 0 ||
        (matchRoster?.awayTeam?.ins?.length ?? 0) > 0 ||
        (matchRoster?.awayTeam?.outs?.length ?? 0) > 0;

    const ctaDefaultLink = `${ctaDefaultPath}?fixtureId=${matchId}`;
    const ctaConfig = getCtaConfig(
        ctaDisabled,
        ctaCustomLink,
        ctaDefaultLink,
        ctaCustomText
    );

    const headerData = {
        startTime,
        venueName,
        venueLocation,
        venueLandOwner,
        homeTeamId,
        awayTeamId,
        matchUrl,
        roundName,
        homeTeamName,
        awayTeamName
    };

    const renderSkeletonInsOuts = () => {
        if (teamDisplay === 'both') {
            return (
                <>
                    <InsOutsBothSkeleton />
                    <InsOutsBothSkeleton />
                </>
            );
        }
        return <InsOutsSingleSkeleton />;
    };

    const renderMainContent = () => {
        if (isFetchingMatchRosterData) {
            return (
                <>
                    <TLCondensedHeaderSkeleton teamDisplay={teamDisplay} />
                    {renderSkeletonInsOuts()}
                    <TLCondensedCtaSkeleton />
                    <TLCondensedLastUpdatedSkeleton />
                </>
            );
        }

        if (matchRosterDataError || !rosterData || !matchRoster) {
            return (
                <>
                    <div className="team-lineups__section">
                        <div className="team-lineups-condensed-embeddable__no-data">
                            <p className="team-lineups-condensed-embeddable__no-data-text">
                                {getTranslation(
                                    'label.teamLineups.notAvailable'
                                )}
                            </p>
                        </div>
                        {ctaConfig && <TLCondensedCta ctaConfig={ctaConfig} />}
                    </div>
                    <LastUpdated
                        cssClass="team-lineups__last-updated"
                        lastUpdated={lastUpdated}
                        selectedRegion={selectedRegion}
                    />
                </>
            );
        }

        if (insOutsExists) {
            return (
                <>
                    <InsOuts
                        matchRoster={matchRoster}
                        matchTitle={matchTitle}
                        teamDisplay={teamDisplay}
                        ctaConfig={ctaConfig}
                        homeTeamId={homeTeamId}
                        awayTeamId={awayTeamId}
                        isAFLW={isAFLW}
                    />
                    <LastUpdated
                        cssClass="team-lineups__last-updated"
                        lastUpdated={lastUpdated}
                        selectedRegion={selectedRegion}
                    />
                </>
            );
        }

        return (
            <>
                <div className="team-lineups__section">
                    <div className="team-lineups-condensed-embeddable__no-changes">
                        <p className="team-lineups-condensed-embeddable__no-changes-text">
                            {getTranslation('label.teamLineups.noChanges')}
                        </p>
                    </div>
                    {ctaConfig && (
                        <TLCondensedCta
                            ctaConfig={ctaConfig}
                            matchTitle={matchTitle}
                        />
                    )}
                </div>
                <LastUpdated
                    cssClass="team-lineups__last-updated"
                    lastUpdated={lastUpdated}
                    selectedRegion={selectedRegion}
                />
            </>
        );
    };

    return (
        <>
            {!isFetchingMatchRosterData && (
                <>
                    <TLCondensedHeader
                        match={headerData}
                        teamDisplay={teamDisplay}
                    />
                    {sponsorPromoId && (
                        <Sponsor
                            promoId={sponsorPromoId}
                            skeletonCssClass="team-lineups-condensed-embeddable"
                        />
                    )}
                </>
            )}

            {renderMainContent()}
        </>
    );
};

TLCondensed.propTypes = {
    matchId: PropTypes.string.isRequired,
    matchTitle: PropTypes.string.isRequired,
    matchUrl: PropTypes.string.isRequired,
    roundName: PropTypes.string.isRequired,
    providerMatchId: PropTypes.string.isRequired,
    providerRoundId: PropTypes.string.isRequired,
    homeTeamId: PropTypes.string.isRequired,
    awayTeamId: PropTypes.string.isRequired,
    homeTeamName: PropTypes.string.isRequired,
    awayTeamName: PropTypes.string.isRequired,
    teamDisplay: PropTypes.string.isRequired,
    ctaDefaultPath: PropTypes.string,
    ctaDisabled: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    ctaCustomLink: PropTypes.string,
    ctaCustomText: PropTypes.string,
    startTime: PropTypes.string.isRequired,
    venueName: PropTypes.string.isRequired,
    venueLocation: PropTypes.string.isRequired,
    venueLandOwner: PropTypes.string.isRequired,
    sponsorPromoId: PropTypes.string,
    competitionId: PropTypes.string
};
